import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
  PHONE_REGEX,
  RFC_REGEX,
  wrongPasswordMessage,
} from '@/constants/regex';
import { useGetFiscalRegimes } from '@/hooks/fiscal/useGetFiscalRegimes';
import { fiscalRegimensResponseToSelectOptionsUi } from '@/hooks/fiscal/useGetFiscalRegimes/mapper';
import { useCreateSeller } from '@/hooks/sellers/useCreateSeller';
import { safeParseNumber } from '@/utils/functions';
import { SelectChangeEvent } from '@mui/material';
import { WritableDraft } from 'immer';
import { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { AddSellerDialogUi, initialState } from './AddSellerDialogUi';

export function useAddSellerDialogViewController(handleClose: () => void) {
  const [uiState, setUiState] = useImmer(initialState);
  const fiscalRegimesHook = useGetFiscalRegimes();

  const createSellerHook = useCreateSeller();

  useEffect(() => {
    if (!fiscalRegimesHook.data) return;
    setUiState((draft) => {
      draft.fiscalRegimeOptions = fiscalRegimensResponseToSelectOptionsUi(
        fiscalRegimesHook.data
      );
      calculateSaveButtonState(draft);
    });
  }, [fiscalRegimesHook.data]);

  const onChangedBusinessName = (newValue: string) =>
    setUiState((draft) => {
      draft.businessName = newValue;
      draft.businessNameError = !newValue.trim()
        ? 'Nombre de negocio inválido'
        : undefined;
      calculateSaveButtonState(draft);
    });

  const onChangedResponsiblename = (newValue: string) =>
    setUiState((draft) => {
      draft.responsibleName = newValue;
      draft.responsibleNameError = !newValue.trim()
        ? 'Nombre del responsable inválido'
        : undefined;
      calculateSaveButtonState(draft);
    });

  const onChangedRfc = (newValue: string) =>
    setUiState((draft) => {
      draft.rfc = newValue;
      draft.rfcError = !RFC_REGEX.test(draft.rfc) ? 'RFC inválido' : undefined;
      calculateSaveButtonState(draft);
    });

  const onChangedFiscalRegime = (event: SelectChangeEvent) =>
    setUiState((draft) => {
      draft.fiscalRegime = event.target.value;
      calculateSaveButtonState(draft);
    });

  const onChangedLegalName = (newValue: string) =>
    setUiState((draft) => {
      draft.legalName = newValue;
      draft.legalNameError = !newValue.trim()
        ? 'Nombre legal inválido'
        : undefined;
      calculateSaveButtonState(draft);
    });

  const onChangedPhone = (newValue: string) =>
    setUiState((draft) => {
      draft.phoneNumber = newValue;
      draft.phoneNumberError = !PHONE_REGEX.test(newValue)
        ? 'Teléfono inválido'
        : undefined;
      calculateSaveButtonState(draft);
    });

  const onChangedPurchaseMinAmount = (newValue: string) =>
    setUiState((draft) => {
      draft.purchaseMinAmount = `${safeParseNumber(newValue) ?? ''}`;
      calculateSaveButtonState(draft);
    });

  const onChangedEmail = (newValue: string) =>
    setUiState((draft) => {
      draft.email = newValue;
      draft.emailError = EMAIL_REGEX.test(newValue)
        ? undefined
        : 'Correo inválido';
      calculateSaveButtonState(draft);
    });

  const onChangedPassword = (newValue: string) =>
    setUiState((draft) => {
      draft.password = newValue;
      draft.passwordError = !PASSWORD_REGEX.test(newValue)
        ? wrongPasswordMessage
        : undefined;
      calculateSaveButtonState(draft);
    });

  const calculateSaveButtonState = (
    draft: WritableDraft<AddSellerDialogUi>
  ) => {
    const saveEnabled =
      draft.businessName.trim() &&
      draft.responsibleName.trim() &&
      RFC_REGEX.test(draft.rfc) &&
      draft.fiscalRegime.trim() &&
      draft.legalName.trim() &&
      PHONE_REGEX.test(draft.phoneNumber) &&
      safeParseNumber(draft.purchaseMinAmount) !== undefined &&
      EMAIL_REGEX.test(draft.email) &&
      PASSWORD_REGEX.test(draft.password);
    draft.saveDisabled = !saveEnabled;
  };

  const onClickedSave = () =>
    createSellerHook.mutate(uiState, {
      onSuccess: () => {
        setUiState(initialState);
        handleClose();
      },
    });

  return {
    uiState,
    actions: {
      onChangedBusinessName,
      onChangedResponsiblename,
      onChangedRfc,
      onChangedFiscalRegime,
      onChangedLegalName,
      onChangedPhone,
      onChangedPurchaseMinAmount,
      onChangedEmail,
      onChangedPassword,
      onClickedSave,
    },
  };
}
